import * as React from "react";
import { Toast, ToastClose, ToastTitle, ToastDescription, ToastPrimitives, type ToastProps } from "../components/ui/toast";
import { ToastContext } from "../hooks/use-toast";

type ToastState = ToastProps & { open: boolean };

export function ToastProvider({ children }: { children: React.ReactNode }) {
	const [toasts, setToasts] = React.useState<ToastState[]>([]);

	const showToast = React.useCallback(({ title, description, status, duration = 5000 }: Omit<ToastProps, "id">) => {
		const id = Math.random().toString(36).substring(2, 9);
		setToasts((prev) => [...prev, { id, title, description, status, duration, open: true }]);
	}, []);

	const closeToast = React.useCallback((id: string) => {
		// First set open to false to trigger close animation
		setToasts((prev) => prev.map((toast) => (toast.id === id ? { ...toast, open: false } : toast)));

		// Then remove after animation completes (150ms matches our CSS animation)
		setTimeout(() => {
			setToasts((prev) => prev.filter((toast) => toast.id !== id));
		}, 150);
	}, []);

	return (
		<ToastContext.Provider value={{ showToast }}>
			{children}
			<ToastPrimitives.Provider swipeDirection="down">
				{toasts.map((toast) => (
					<Toast
						key={toast.id}
						open={toast.open}
						status={toast.status}
						duration={toast.duration}
						onOpenChange={(open) => {
							if (!open) {
								closeToast(toast.id);
							}
						}}
					>
						<div className="flex items-center gap-1.5">
							<ToastTitle>{toast.title}</ToastTitle>
							{toast.description && <ToastDescription>{toast.description}</ToastDescription>}
						</div>
						<ToastClose />
					</Toast>
				))}
				<ToastPrimitives.Viewport className="fixed bottom-0 left-1/2 -translate-x-1/2 z-100 flex flex-col items-center gap-2 p-6" />
			</ToastPrimitives.Provider>
		</ToastContext.Provider>
	);
}
